import type { LogLevel } from './types';

type LogCategory = 
  | 'Initialization'
  | 'API'
  | 'Translation'
  | 'Navigation'
  | 'Maps'
  | 'Cache'
  | 'Events'
  | 'PWA'
  | 'Analytics'
  | 'Performance'
  | 'UI'
  | 'Routing'
  | 'Error'
  | 'RGPD'
  | 'User'
  | 'Stay'
  | 'Calendar'
  | 'PDF'
  | 'Network'
  | 'Audio'
  | 'Debug'
  | 'Favorites'
  | 'Review';

interface LogOptions {
  category: LogCategory;
  data?: unknown;
}

class Logger {
  private static instance: Logger;
  private isDebugMode: boolean;
  private logLevel: LogLevel;
  private enabledCategories: Set<LogCategory>;
  private categoryColors: Record<LogCategory, string> = {
    Initialization: '#10b981',
    API: '#3b82f6',
    Translation: '#8b5cf6',
    Navigation: '#ec4899',
    Maps: '#f59e0b',
    Cache: '#6366f1',
    Events: '#14b8a6',
    PWA: '#f97316',
    Analytics: '#84cc16',
    Performance: '#ef4444',
    UI: '#06b6d4',
    Routing: '#8b5cf6',
    Error: '#dc2626',
    RGPD: '#9333ea',
    User: '#0ea5e9',
    Stay: '#f43f5e',
    Calendar: '#0891b2',
    PDF: '#4f46e5',
    Network: '#0d9488',
    Audio: '#7c3aed',
    Debug: '#94a3b8',
    Favorites: '#f59e0b',
    Review: '#8b5cf6'
  };

  private constructor() {
    this.logLevel = this.getLogLevel();
    this.isDebugMode = this.initializeDebugMode();
    this.enabledCategories = this.getEnabledCategories();
  }

  private getLogLevel(): LogLevel {
    try {
      const level = import.meta.env.VITE_LOG_LEVEL?.toLowerCase() as LogLevel;
      if (['debug', 'info', 'warn', 'error'].includes(level)) {
        return level;
      }
      return 'info';
    } catch (error) {
      return 'info';
    }
  }

  private getEnabledCategories(): Set<LogCategory> {
    try {
      const categoriesStr = import.meta.env.VITE_LOG_CATEGORIES;
      if (!categoriesStr) {
        // Si aucune catégorie n'est spécifiée, activer toutes les catégories
        return new Set(Object.keys(this.categoryColors) as LogCategory[]);
      }
      
      const categories = categoriesStr.split(',').map(c => c.trim()) as LogCategory[];
      return new Set(categories);
    } catch (error) {
      // En cas d'erreur, activer toutes les catégories
      return new Set(Object.keys(this.categoryColors) as LogCategory[]);
    }
  }

  private initializeDebugMode(): boolean {
    try {
      // En production, utiliser VITE_LOG_FORCE_PROD pour forcer les logs
      if (import.meta.env.PROD) {
        return import.meta.env.VITE_LOG_FORCE_PROD === 'true';
      }
      // En développement, toujours activer les logs
      return true;
    } catch (error) {
      return process.env.NODE_ENV === 'development';
    }
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private formatMessage(level: LogLevel, message: string, options: LogOptions): string {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, -1);
    const category = `[${options.category}]`;
    return `${timestamp} ${level.toUpperCase()} ${category} ${message}`;
  }

  private shouldLog(level: LogLevel, category: LogCategory): boolean {
    // Vérifier si le niveau de log est suffisant
    const logLevels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const currentLevelIndex = logLevels.indexOf(this.logLevel);
    const messageLevelIndex = logLevels.indexOf(level);
    
    if (messageLevelIndex < currentLevelIndex) {
      return false;
    }
    
    // Vérifier si la catégorie est activée
    if (!this.enabledCategories.has(category)) {
      return false;
    }
    
    // En mode production, ne logger que si le mode debug est activé
    if (import.meta.env.PROD && !this.isDebugMode && level !== 'error') {
      return false;
    }
    
    return true;
  }

  private logWithColor(level: LogLevel, message: string, options: LogOptions): void {
    if (!this.shouldLog(level, options.category)) {
      return;
    }

    const color = this.categoryColors[options.category];
    const formattedMessage = this.formatMessage(level, message, options);
    
    let style = `color: ${color}; font-weight: bold;`;
    if (level === 'error') {
      style += ' background: #fee2e2; padding: 2px 4px; border-radius: 2px;';
    } else if (level === 'warn') {
      style += ' background: #fef3c7; padding: 2px 4px; border-radius: 2px;';
    }

    const emoji = {
      Initialization: '🚀',
      API: '🔌',
      Translation: '🌐',
      Navigation: '🧭',
      Maps: '🗺️',
      Cache: '💾',
      Events: '🎯',
      PWA: '📱',
      Analytics: '📊',
      Performance: '⚡',
      UI: '🎨',
      Routing: '🔄',
      Error: '❌',
      RGPD: '🔒',
      User: '👤',
      Stay: '🏨',
      Calendar: '📅',
      PDF: '📄',
      Network: '🌐',
      Audio: '🔊',
      Debug: '🐞',
      Favorites: '❤️',
      Review: '⭐'
    }[options.category];

    console.log(
      `%c${emoji} ${formattedMessage}`,
      style,
      options.data || ''
    );
  }

  public debug(message: string, options: LogOptions): void {
    this.logWithColor('debug', message, options);
  }

  public info(message: string, options: LogOptions): void {
    this.logWithColor('info', message, options);
  }

  public warn(message: string, options: LogOptions): void {
    this.logWithColor('warn', message, options);
  }

  public error(message: string, options: LogOptions): void {
    this.logWithColor('error', message, options);
  }

  // Méthode pour mettre à jour la configuration des logs
  public updateConfig(config: {
    logLevel?: LogLevel;
    enabledCategories?: LogCategory[];
    isDebugMode?: boolean;
  }): void {
    if (config.logLevel) {
      this.logLevel = config.logLevel;
    }
    
    if (config.enabledCategories) {
      this.enabledCategories = new Set(config.enabledCategories);
    }
    
    if (config.isDebugMode !== undefined) {
      this.isDebugMode = config.isDebugMode;
    }
    
  }
}

export const logger = Logger.getInstance();